<template>
  <div>
    <div
      ref="ganttChartContainer"
      class="gantt-chart-container"
      @pointerdown="dragMouseDown"
      @pointermove="dragMouseMove"
      @pointerup="dragMouseUp"
      @wheel="onMouseWheel"
    >
      <div ref="itemChartCanvasContainer">
        <ItemChartCanvas
          ref="itemChartCanvas"
          :config-obj="configObj"
          :categories="displayCategories"
          :lines="displayLines"
          :today-date="todayDate"
          :current-scroll-position="currentScrollPosition"
          :side-bar-width="sideBarWidth"
          :is-desktop="isDesktop"
          @on-show-hover="showHover"
          @on-hide-hover="hideHover"
        />
      </div>
      <div
        class="line-titles"
        :style="`--side-bar-width: ${sideBarWidth}px; --date-bar-height: ${itemChartCanvas?.dateBarHeight}px; --gantt-line-height: ${configObj.scaleY}px`"
      >
        <div class="inner" :style="scrollTopStyle">
          <div
            v-for="(category, i) in displayCategories"
            :id="`gantt-category-title-${category.id}`"
            :key="i"
            class="category-line"
            :class="{ 'hidden-line': category.hidden }"
          >
            <div class="category-header-row">
              <div
                class="title-sidebar"
                @click="setCategoryHidden({ id: category.id, hidden: !category.hidden })"
                @pointerdown.prevent
              >
                <span class="title-sidebar-label"
                  ><img class="arrow" src="@/assets/gantt-arrow.png" />{{ category.title }}</span
                >
                <span class="title-sidebar-count">{{ category.lines?.length ?? 0 }}</span>
                <button
                  v-if="category.users?.length"
                  type="button"
                  class="users-toggle"
                  :title="
                    areCategoryUsersVisible(category.id) ? 'Hide active users' : 'Show active users'
                  "
                  @click.stop="toggleCategoryUsers(category.id)"
                  @pointerdown.stop
                >
                  <PhoneOffIcon v-if="areCategoryUsersVisible(category.id)" class="size-3" />
                  <PhoneIcon v-else class="size-3" />
                </button>
              </div>
              <div class="title-chart-area" :title="category.description ?? undefined">
                <span
                  v-if="category.users?.length && areCategoryUsersVisible(category.id)"
                  class="title-chart-area-users"
                >
                  <template v-for="(u, idx) in category.users" :key="u.id">
                    <span v-if="idx > 0" class="title-chart-area-sep">, </span>
                    <span class="title-chart-area-user">
                      <span>{{ userLabel(u) }}: </span>
                      <span v-if="u.phone?.trim()" class="inline-flex items-center gap-0.5">
                        <a class="title-chart-area-link" :href="telHref(u.phone)" @pointerdown.stop>
                          {{ u.phone.trim() }}
                        </a>
                        <span class="pointer-events-auto inline-flex" @pointerdown.stop>
                          <CallButton :phone-number="u.phone" class="!ml-1" />
                        </span>
                      </span>
                      <span v-else class="title-chart-area-text">—</span>
                    </span>
                  </template>
                </span>
                <span v-else-if="category.description" class="title-chart-area-text">{{
                  category.description
                }}</span>
              </div>
            </div>
            <div v-if="!category.hidden" class="lines">
              <div
                v-for="(line, o) in category.lines"
                :id="`gantt-line-title-${line.id}`"
                :key="o"
                class="line"
                :style="{ backgroundColor: line.color ?? '#fff' }"
              >
                <router-link
                  v-if="$can('manage', 'Car')"
                  :to="{ name: 'viewCar', params: { id: line.id } }"
                  class="hover:underline"
                >
                  {{ line.title }}
                </router-link>
                <div v-else class="cursor-default">{{ line.title }}</div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isDesktop"
          class="sidebar-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          @pointerdown="handleSidebarResizeStart"
        />
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="showHoverBox && hoverBoxContent"
        ref="hoverBox"
        :style="floatingStyles"
        :class="float({ class: 'pointer-events-none z-50 p-3 text-sm' })"
      >
        <div ref="hoverArrow" :style="hoverArrowStyles" class="gantt-hover-arrow" aria-hidden />
        <GanttHoverCard :content="hoverBoxContent" />
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers';
import { DateTime } from 'luxon';
import { computed, onBeforeUnmount, reactive, ref, toRef, watch, watchEffect } from 'vue';
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { float } from '@carvia/theme/lib/cva/float';
import { PhoneIcon, PhoneOffIcon } from 'lucide-vue-next';
import type {
  Block,
  BlockInternal,
  Category,
  CategoryInternal,
  CategoryUser,
  Config,
  DragState,
  GanttHoverContent,
  HideInterval,
  Line,
  LineInternal,
  LineType,
  Tag,
} from './interfaces';
import CallButton from '@/components/CallButton.vue';
import ItemChartCanvas from './ItemChartCanvas.vue';
import GanttHoverCard from './GanttHoverCard.vue';
import { connectTransferBars, TRANSFER_JOIN_RADIUS_PX } from './connect-transfer-bars';
import { clamp, throttle } from 'lodash';
import { useMousePosition } from '@/hooks/use-mouse-position';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { useBreakpoints, useLocalStorage } from '@vueuse/core';
import { cvBreakpoints } from '@/utils/breakpoints.ts';

const props = defineProps<{
  categories?: Category[];
  lines?: Line[];
  blocks?: Block[];
  lineTypes?: LineType[];
  tags?: Tag[];
}>();

const emit = defineEmits<{
  (e: 'onChangeDate', value: DateTime): void;
  (
    e: 'onCalendarClick',
    x: number,
    y: number,
    line: LineInternal | undefined,
    date: DateTime,
  ): void;
}>();

const scrollX = useRouteQueryRef('x', 0, 'number');
const scrollY = useRouteQueryRef('y', 0, 'number');
const visibleCategoryIds = useRouteQueryRef('categories', undefined, 'string', {
  isArray: true,
  emptyArrayTo: 'undefined',
});

function telHref(phone: string) {
  return `tel:${phone.replace(/\s/g, '')}`;
}

function userLabel(u: CategoryUser) {
  return `${u.firstName} ${u.lastName}`.trim();
}

const isDesktop = useBreakpoints(cvBreakpoints).greaterOrEqual('lg');
const isLandscapeOrGreater = useBreakpoints(cvBreakpoints).greaterOrEqual('md');

const SIDEBAR_WIDTH_STORAGE_KEY = 'gantt-sidebar-width';
const DEFAULT_SIDEBAR_WIDTH = 350;
const MIN_SIDEBAR_WIDTH = 130;
const MAX_SIDEBAR_WIDTH = 600;
const MOBILE_SIDEBAR_WIDTH = 130;
const MOBILE_LANDSCAPE_SIDEBAR_WIDTH = 270;

const mobileSidebarWidth = computed(() =>
  isLandscapeOrGreater.value ? MOBILE_LANDSCAPE_SIDEBAR_WIDTH : MOBILE_SIDEBAR_WIDTH,
);

const storedSidebarWidth = useLocalStorage(SIDEBAR_WIDTH_STORAGE_KEY, DEFAULT_SIDEBAR_WIDTH);
const sideBarWidth = computed(() =>
  isDesktop.value
    ? clamp(storedSidebarWidth.value, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH)
    : mobileSidebarWidth.value,
);

function handleSidebarResizeStart(e: PointerEvent) {
  if (!isDesktop.value) {
    return;
  }
  e.preventDefault();
  e.stopPropagation();

  const startX = e.clientX;
  const startWidth = sideBarWidth.value;

  const handleMove = (moveEvent: PointerEvent) => {
    storedSidebarWidth.value = clamp(
      startWidth + moveEvent.clientX - startX,
      MIN_SIDEBAR_WIDTH,
      MAX_SIDEBAR_WIDTH,
    );
  };

  const handleEnd = () => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleEnd);
    window.removeEventListener('pointercancel', handleEnd);
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
  };

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('pointermove', handleMove);
  window.addEventListener('pointerup', handleEnd);
  window.addEventListener('pointercancel', handleEnd);
}

const todayDate = ref(DateTime.now().startOf('day'));
let dateOfCurrentScrollPosition: DateTime;

const currentScrollPosition = reactive({
  x: scrollX.value ?? 0,
  y: scrollY.value ?? 0,
});

watch([scrollX, scrollY], ([x, y]) => {
  currentScrollPosition.x = x ?? 0;
  currentScrollPosition.y = y ?? 0;
});

const ganttChartContainer = ref<HTMLElement>();
const itemChartCanvas = ref<ComponentExposed<typeof ItemChartCanvas>>();
const itemChartCanvasContainer = ref<HTMLElement>();
const hoverBox = ref<HTMLElement | null>(null);
const hoverArrow = ref<HTMLElement | null>(null);
const hoverBoxContent = ref<GanttHoverContent | null>(null);
const showHoverBox = ref(false);
const hoverBar = ref({ y: 0, height: 26 });
const mousePosition = useMousePosition();
const hoverAnchor = {
  getBoundingClientRect: () => {
    const canvasTop = itemChartCanvasContainer.value?.getBoundingClientRect().top ?? 0;
    const x = mousePosition.value.x;
    const y = canvasTop + hoverBar.value.y;
    const { height } = hoverBar.value;
    return { width: 0, height, x, y, top: y, left: x, right: x, bottom: y + height };
  },
  get contextElement() {
    return itemChartCanvasContainer.value;
  },
};
const {
  floatingStyles,
  middlewareData,
  placement,
  update: updateHoverPosition,
} = useFloating(ref(hoverAnchor), hoverBox, {
  placement: 'top',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(10),
    flip(),
    shift({ padding: 8 }),
    arrow({ element: hoverArrow, padding: 8 }),
  ],
});

const hoverArrowStyles = computed(() => {
  const data = middlewareData.value?.arrow;
  const side = placement.value?.split('-')[0] ?? 'top';
  const base: Record<string, string> = {
    marginTop: '-4px',
    transform: side === 'bottom' ? 'rotate(225deg)' : 'rotate(45deg)',
  };
  switch (side) {
    case 'top':
      base.top = '100%';
      base.left = data?.x != null ? `${data.x}px` : '50%';
      break;
    case 'bottom':
      base.top = '0';
      base.left = data?.x != null ? `${data.x}px` : '50%';
      break;
    case 'left':
      base.left = '100%';
      base.top = data?.y != null ? `${data.y}px` : '50%';
      break;
    case 'right':
      base.left = '0';
      base.top = data?.y != null ? `${data.y}px` : '50%';
      break;
    default:
      base.left = data?.x != null ? `${data.x}px` : '50%';
      base.top = data?.y != null ? `${data.y}px` : '50%';
  }
  return base;
});

let configObj: Config = {
  scaleX: 30,
  scaleY: 26,
};

const categoriesRef = ref<CategoryInternal[]>([]);
watchEffect(() => {
  categoriesRef.value =
    props.categories?.map((category) => ({
      ...category,
      hidden: categoriesRef.value.some((c) => c.id === category.id && !c.hidden)
        ? false
        : category.hidden,
    })) ?? [];
});

const displayCategories = computed(() =>
  categoriesRef.value?.map((category) => ({
    ...category,
    lines: displayLines.value?.filter((line) => line.category === category.id) ?? [],
  })),
);

const setCategoryHidden = (setHidden: { id: string; hidden: boolean }) => {
  if (!categoriesRef.value.length) {
    return;
  }
  for (const category of categoriesRef.value) {
    if (category.id === setHidden.id) {
      category.hidden = setHidden.hidden;
      clampYScrollPosition();
    }
  }
};

const linesRef = ref<LineInternal[]>(props.lines ?? []);
watchEffect(() => (linesRef.value = props.lines ?? []));

const displayLines = computed(() => {
  const displayAvailableBufferDays = 7;
  const maxVisibleFrom =
    itemChartCanvas.value?.lastVisibleDay?.plus({
      days: displayAvailableBufferDays,
    }) ?? 0;
  const minVisibleUntil =
    itemChartCanvas.value?.firstVisibleDay?.minus({
      days: displayAvailableBufferDays,
    }) ?? 0;
  const isHiddenInterval = ({ hideFrom, hideUntil }: HideInterval) =>
    (!hideFrom || hideFrom < minVisibleUntil) && (!hideUntil || hideUntil > maxVisibleFrom);
  const isVisible = (line: LineInternal) => {
    if (line.hiddenByLineType || line.hiddenByTags) {
      return false;
    }
    const isAlreadyAvailable = !line.visibleFrom || line.visibleFrom < maxVisibleFrom;
    const isStillAvailable = !line.visibleUntil || line.visibleUntil > minVisibleUntil;
    return isAlreadyAvailable && isStillAvailable && !line.hide?.some(isHiddenInterval);
  };
  return linesRef.value?.filter(isVisible).map((line) => {
    return {
      ...line,
      blocks: blockMap.value.get(line.id) || [],
    };
  });
});

const blocks = computed<BlockInternal[]>(() => {
  const internals =
    props.blocks?.map((block) => ({
      ...block,
      title: block.title.replace(' ', '\xa0'),
      tagObj: tags.value?.find((tag) => tag.slug === block.tag),
      xStartPosInDays: block.startDate && block.startDate.diff(todayDate.value).as('days'),
      xEndPosIndDays: block.endDate && block.endDate.diff(todayDate.value).as('days'),
      xPos: 0,
      yPos: 0,
      width: 0,
      hover: block.hover,
      shortenedTitle: '',
      roundLeft: true,
      roundRight: true,
      textPadLeftDays: 0,
      textPadRightDays: 0,
    })) ?? [];
  connectTransferBars(internals, {
    radiusPx: TRANSFER_JOIN_RADIUS_PX,
    pxPerDay: configObj.scaleX,
  });
  return internals;
});

const blockMap = computed<Map<string, BlockInternal[]>>(() => {
  const map = new Map();
  for (const block of blocks.value) {
    if (!map.has(block.lineId)) map.set(block.lineId, []);
    map.get(block.lineId).push(block);
  }
  return map;
});

const lineTypes = toRef(props, 'lineTypes');
const tags = ref(props.tags);

let activeLineTypeFilters: {
  typeName: string;
  type: { tag: string; title: string };
}[] = [];
const categoriesWithVisibleUsers = ref<Set<string>>(new Set());

const areCategoryUsersVisible = (categoryId: string) =>
  categoriesWithVisibleUsers.value.has(categoryId);

const toggleCategoryUsers = (categoryId: string) => {
  const next = new Set(categoriesWithVisibleUsers.value);
  if (next.has(categoryId)) {
    next.delete(categoryId);
  } else {
    next.add(categoryId);
  }
  categoriesWithVisibleUsers.value = next;
};

const TAP_DISTANCE_PX = 5;

const dragState: DragState = {
  scrollTop: 0,
  scrollLeft: 0,
  startMouseX: 0,
  startMouseY: 0,
  currentMouseX: 0,
  currentMouseY: 0,
  lastMouseX: 0,
  lastMouseY: 0,
  lastmouseMoveTime: 0,
  mouseMoveTime: 0,
  slideX: 0,
  slideY: 0,
  holding: false,
  dragging: false,
  isTouch: false,
  animationPrevTS: null,
  animating: false,
  stopped: false,
  startTime: 0,
};

const scrollTopStyle = computed(() => `top: -${currentScrollPosition.y}px;`);

const showHover = (content: GanttHoverContent, yPos: number) => {
  // Touch: the finger moving over bars must not open the card; taps open it via onBlockClick.
  if (dragState.isTouch && dragState.holding) {
    hideHover();
    return;
  }
  hoverBoxContent.value = content;
  hoverBar.value = { y: yPos, height: configObj.scaleY };
  showHoverBox.value = true;
  updateHoverPosition();
};

const hideHover = () => {
  showHoverBox.value = false;
};

watch(mousePosition, () => {
  if (showHoverBox.value) updateHoverPosition();
});

const dragMouseDown = (e: PointerEvent) => {
  if (e.defaultPrevented) {
    return;
  }
  e.preventDefault();

  updateScrollPositionInRoute();

  if (dragState.slideX !== 0 || dragState.slideY !== 0) {
    dragState.stopped = true;
  }

  dragState.holding = true;
  dragState.isTouch = e.pointerType === 'touch';
  dragState.scrollLeft = currentScrollPosition.x ?? 0;
  dragState.scrollTop = currentScrollPosition.y ?? 0;
  dragState.startMouseX = e.clientX;
  dragState.startMouseY = e.clientY;
  dragState.slideX = 0;
  dragState.slideY = 0;
  dragState.lastMouseX = dragState.startMouseX;
  dragState.lastMouseY = dragState.startMouseY;
  dragState.currentMouseX = 0;
  dragState.currentMouseY = 0;
  dragState.mouseMoveTime = 0;
  dragState.startTime = Date.now();

  if (itemChartCanvasContainer.value) {
    itemChartCanvasContainer.value.style.cursor = 'grabbing';
    itemChartCanvasContainer.value.style.userSelect = 'none';
  }

  dragState.animationPrevTS = null;

  if (dragState.animating === false) {
    window.requestAnimationFrame(slideStep);
  }
};

const dragMouseMove = (e: PointerEvent) => {
  if (!dragState.holding) {
    return;
  }

  const xDistance = e.clientX - dragState.startMouseX;
  const yDistance = e.clientY - dragState.startMouseY;
  // Dead zone: don't capture the pointer for a tap, otherwise `click` never reaches sidebar links.
  if (!dragState.dragging) {
    if (Math.abs(xDistance) + Math.abs(yDistance) < TAP_DISTANCE_PX) return;
    ganttChartContainer.value?.setPointerCapture(e.pointerId);
    if (dragState.isTouch) hideHover();
  }
  currentScrollPosition.x = dragState.scrollLeft - xDistance;
  currentScrollPosition.y = dragState.scrollTop - yDistance;
  clampYScrollPosition();
  dragState.dragging = true;

  dragState.lastMouseX = dragState.currentMouseX;
  dragState.lastMouseY = dragState.currentMouseY;
  dragState.currentMouseX = e.clientX;
  dragState.currentMouseY = e.clientY;
  const timestamp = new Date().getTime();
  dragState.mouseMoveTime = timestamp - dragState.lastmouseMoveTime;
  dragState.lastmouseMoveTime = timestamp;
};

const dragMouseUp = (e: PointerEvent) => {
  ganttChartContainer.value?.releasePointerCapture(e.pointerId);

  updateScrollPositionInRoute();

  if (!dragState.holding) {
    return;
  }

  if (itemChartCanvasContainer.value) {
    itemChartCanvasContainer.value.style.removeProperty('cursor');
    itemChartCanvasContainer.value.style.removeProperty('user-select');
  }
  dragState.holding = false;
  dragState.dragging = false;
  dragState.stopped = false;
  if (dragState.mouseMoveTime > 0) {
    dragState.slideX = (dragState.lastMouseX - dragState.currentMouseX) / dragState.mouseMoveTime;
    dragState.slideY = (dragState.lastMouseY - dragState.currentMouseY) / dragState.mouseMoveTime;
  }

  if (
    Math.abs(dragState.startMouseX - e.clientX) + Math.abs(dragState.startMouseY - e.clientY) <
      TAP_DISTANCE_PX &&
    Date.now() - dragState.startTime < 500
  ) {
    if (itemChartCanvas.value) {
      itemChartCanvas.value.onBlockClick(dragState.isTouch && !showHoverBox.value);
      itemChartCanvas.value.onCalendarClick((line: LineInternal | undefined, date: DateTime) =>
        emit('onCalendarClick', e.clientX, e.clientY, line, date),
      );
    }
  }
};

const onMouseWheel = (e: WheelEvent) => {
  e.preventDefault();
  currentScrollPosition.x += e.deltaX;
  currentScrollPosition.y += e.deltaY;
  clampYScrollPosition();
  updateScrollDate();
  if (dragState.animating) return;
  itemChartCanvas.value?.loop();
};

const slideStep = (timestamp: number) => {
  dragState.animating = true;

  if (dragState.animationPrevTS === null) {
    dragState.animationPrevTS = timestamp;
  }
  const delta = timestamp - dragState.animationPrevTS;
  dragState.animationPrevTS = timestamp;

  let slideX = dragState.slideX;
  let slideY = dragState.slideY;

  const noMovement = slideX === 0 && slideY === 0;

  if (!dragState.holding) {
    if (!noMovement) {
      const originalSpeed = Math.abs(Math.sqrt(Math.pow(slideX, 2) + Math.pow(slideY, 2)));
      const speed = Math.min(originalSpeed, 3);

      const newSpeed = speed - 0.02 - (1 / originalSpeed) * (originalSpeed - speed);

      if (newSpeed <= 0) {
        dragState.animating = false;
        dragState.slideX = 0;
        dragState.slideY = 0;
        updateScrollDate.flush();
        return;
      }

      const factor = (1 / speed) * newSpeed;

      slideX = slideX * factor;
      slideY = slideY * factor;

      currentScrollPosition.x = currentScrollPosition.x + slideX * delta;
      currentScrollPosition.y = currentScrollPosition.y + slideY * delta;
      clampYScrollPosition();

      dragState.slideX = slideX;
      dragState.slideY = slideY;
      updateScrollDate();
    } else {
      updateScrollDate.flush();
      dragState.animating = false;
      dragState.slideX = 0;
      dragState.slideY = 0;
      return;
    }
  }

  itemChartCanvas.value?.loop();
  window.requestAnimationFrame(slideStep);
};

const clampYScrollPosition = () => {
  if (!itemChartCanvas.value) return;
  const maxYScroll =
    (displayCategories.value?.reduce((acc, cur) => {
      acc++;
      if (!cur.hidden) {
        acc += cur.lines.length;
      }
      return acc;
    }, 0) ?? 0) *
      configObj.scaleY -
    itemChartCanvas.value.getCanvasHeight();
  currentScrollPosition.y = Math.max(0, Math.min(maxYScroll, currentScrollPosition.y));
};

let lastLoopTimestamp = 0;

const updateScrollDate = throttle(
  () => {
    if (Date.now() < lastLoopTimestamp + 100) return;
    lastLoopTimestamp = Date.now();

    const newDateOfCurrentScrollPosition = todayDate.value
      .plus({
        days: currentScrollPosition.x / configObj.scaleX,
      })
      .startOf('day');
    if (
      !dateOfCurrentScrollPosition ||
      !newDateOfCurrentScrollPosition.equals(dateOfCurrentScrollPosition)
    ) {
      dateOfCurrentScrollPosition = newDateOfCurrentScrollPosition;
      emit('onChangeDate', dateOfCurrentScrollPosition);
    }
  },
  200,
  {
    leading: false,
  },
);

window.requestAnimationFrame(updateScrollDate);

const filterLinesByType = (typeName: string, typeTag: string) => {
  const lineType = lineTypes.value?.find((lineType) => lineType.typeName === typeName);
  if (!lineType) {
    return;
  }

  const type = lineType.types.find((t) => t.tag === typeTag);
  activeLineTypeFilters = activeLineTypeFilters.filter((type) => type.typeName !== typeName);
  if (type) {
    activeLineTypeFilters.push({ typeName: lineType.typeName, type: type });
  }

  for (const line of linesRef.value ?? []) {
    let filteredOut = false;
    for (const activeFilter of activeLineTypeFilters) {
      if (
        !line.lineTypes?.find(
          (lineType) =>
            lineType.typeName === activeFilter.typeName && lineType.tag === activeFilter.type.tag,
        )
      ) {
        filteredOut = true;
        break;
      }
    }
    line.hiddenByLineType = filteredOut;
  }
  clampYScrollPosition();
};

const filterLinesByTags = (input: string) => {
  if (!linesRef.value) {
    return;
  }
  for (const line of linesRef.value) {
    const connectedTags = line.tags?.join(' ') ?? '';
    if (
      !input
        .toLowerCase()
        .split(' ')
        .every(
          (v) =>
            connectedTags.toLowerCase().includes(v) ||
            connectedTags
              .toLowerCase()
              .replace(/(\s)|(-)/g, '')
              .includes(v),
        )
    ) {
      line.hiddenByTags = true;
    } else {
      line.hiddenByTags = false;
    }
  }
  clampYScrollPosition();
};

const jumpToToday = () => {
  currentScrollPosition.x = 0;
  // Reset lastLoopTimestamp to ensure updateScrollDate runs and emits onChangeDate
  lastLoopTimestamp = 0;
  updateScrollDate();
  updateScrollDate.flush();
};

const updateScrollPositionInRoute = () => {
  if (scrollX.value !== currentScrollPosition.x) {
    scrollX.value = currentScrollPosition.x;
  }
  if (scrollY.value !== currentScrollPosition.y) {
    scrollY.value = currentScrollPosition.y;
  }
};

const updateRouteInterval = setInterval(() => updateScrollPositionInRoute(), 500);
onBeforeUnmount(() => clearInterval(updateRouteInterval));

let categoriesRouteSynced = false;
watch(
  categoriesRef,
  () => {
    if (!categoriesRef.value.length) {
      return;
    }
    if (!categoriesRouteSynced && visibleCategoryIds.value?.length) {
      for (const category of categoriesRef.value) {
        category.hidden = !visibleCategoryIds.value.includes(category.id);
      }
    } else if (categoriesRouteSynced) {
      visibleCategoryIds.value = categoriesRef.value
        .filter((category) => !category.hidden)
        .map((category) => category.id);
    }

    categoriesRouteSynced = true;
  },
  { immediate: true, deep: true },
);

defineExpose({
  filterLinesByType,
  filterLinesByTags,
  jumpToToday,
});
</script>

<style scoped>
.gantt-chart-container {
  @apply relative size-full touch-none overflow-hidden border border-[#ddd] text-[11px] lg:text-[13px];
}

.line-titles {
  --gantt-line-height: 26px;
  @apply pointer-events-none absolute left-0 top-[var(--date-bar-height)] z-[2] h-[calc(100%-var(--date-bar-height))] w-full overflow-hidden bg-transparent;
}

.line-titles .inner {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
}

.category-header-row {
  @apply flex h-[var(--gantt-line-height)] w-full min-w-0 border-b border-b-[#eee];
}

.title-sidebar {
  @apply pointer-events-auto box-border flex h-[var(--gantt-line-height)] w-[var(--side-bar-width)] flex-shrink-0 cursor-pointer items-center justify-between gap-1 whitespace-nowrap border-r border-r-obsidian-300 bg-[#eee] px-[10px] text-[#777];
}

.title-sidebar-label {
  @apply min-w-0 flex-1 truncate leading-[calc(var(--gantt-line-height)-1px)];
}

.title-sidebar-count {
  @apply hidden flex-shrink-0 rounded bg-[#ddd] px-1 py-0.5 text-xs tabular-nums leading-none text-[#666] lg:inline;
}

.title-chart-area {
  @apply pointer-events-none flex min-h-[var(--gantt-line-height)] min-w-0 flex-1 items-center border-b border-b-transparent px-2 leading-[calc(var(--gantt-line-height)-1px)];
}

.title-chart-area-text {
  @apply min-w-0 truncate text-[#777];
}

.title-chart-area-users {
  @apply block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[#777];
}

.title-chart-area-link {
  @apply pointer-events-auto text-[#777] underline decoration-obsidian-400 underline-offset-2 hover:text-primary;
}

.users-toggle {
  @apply pointer-events-auto hidden h-4 w-4 items-center justify-center rounded text-[#777] hover:bg-[#f0f0f0] hover:text-primary lg:inline-flex;
}

.line-titles .lines {
  @apply pointer-events-auto w-[var(--side-bar-width)] border-r border-r-obsidian-300 bg-white;
}

.line-titles .line {
  @apply box-border h-[var(--gantt-line-height)] overflow-hidden truncate whitespace-nowrap border-b border-b-[#eee] px-[5px] leading-[calc(var(--gantt-line-height)-1px)] lg:px-[10px];
}

.line-titles .title-sidebar .arrow {
  display: inline-block;
  margin-right: 5px;
  width: 14px;
  height: 9px;
  background-image: url(data:image/png;base64,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);
  transform: rotate(180deg);
}

.line-titles .category-line.hidden-line .title-sidebar .arrow {
  transform: rotate(0);
}

.sidebar-resize-handle {
  @apply pointer-events-auto absolute top-0 z-[4] h-full w-1.5 cursor-col-resize touch-none;
  left: calc(var(--side-bar-width) - 3px);
}

.sidebar-resize-handle::after {
  content: '';
  @apply absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-obsidian-300 transition-colors;
}

.sidebar-resize-handle:hover::after,
.sidebar-resize-handle:active::after {
  @apply w-0.5 bg-primary;
}

.gantt-hover-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background: white;
  box-shadow: 1px 1px 0 0 rgb(0 0 0 / 0.15);
}
</style>
